import { useRef, useState } from 'react'
import { Paperclip, Loader2, Trash2, Download, Pencil, Check, X, Upload, FileText, ImageOff } from 'lucide-react'
import { postJson, apiDelete } from '../../lib/api.js'
import { toProcurementFile, fileUrl } from '../../lib/purchase.js'
import { uploadFile } from '../../lib/uploadDirect.js'
import ConfirmDialog from '../ConfirmDialog.jsx'

// เอกสารแนบ for a PO — quotations, invoices, receipts. Stored on the PO-level
// `procurement.file[]`, so every write is a full-list PUT through
// POST /api/procurement: upload appends, rename edits one entry, delete removes
// one (with file_set so an emptied list actually saves).
//
// Unlike the rest of the drawer these are NOT part of the draft — a file lands
// in Wasabi the moment it is picked, so holding the list back until บันทึก would
// mean an orphaned object whenever the user cancels. Each action saves at once
// and the parent is told the new list.
/* The upload proxy answers with JSON that already names the cause
 * ({error, status, detail}), and `detail` carries Wasabi's XML verbatim.
 * Swallowing all of that into "(502)" is what turned a bucket-permission
 * problem into a mystery, so surface the useful part. */
function uploadError(err, file) {
  const payload = err?.body || null
  const status = err?.status || 0
  const raw = String(payload?.detail || payload?.error || '')
  const s3 = raw.match(/<Code>([^<]+)<\/Code>/)?.[1] || ''
  const name = file?.name ? `“${file.name}” ` : ''

  if (status === 403 && payload?.error === 'forbidden') return payload.detail || 'ไม่มีสิทธิ์แนบไฟล์'
  if (payload?.error === 'wasabi_forbidden') {
    return `${name}อัปโหลดไม่สำเร็จ — บัญชีของแอปยังไม่มีสิทธิ์เขียนลงที่เก็บไฟล์ “${payload.bucket}” (ต้องเปิดสิทธิ์ที่ Wasabi)`
  }
  if (s3 === 'AccessDenied') return `${name}อัปโหลดไม่สำเร็จ — ไม่มีสิทธิ์เขียนลงที่เก็บไฟล์ (Wasabi AccessDenied) ต้องแก้สิทธิ์ที่ฝั่ง Wasabi`
  if (payload?.error === 'wasabi_put_failed') return `${name}อัปโหลดไม่สำเร็จ — ที่เก็บไฟล์ปฏิเสธ${s3 ? ` (${s3})` : ` (${payload.status})`}`
  if (payload?.error === 'wasabi_not_configured') return 'ยังไม่ได้ตั้งค่าที่เก็บไฟล์ (Wasabi) บนเซิร์ฟเวอร์'
  if (payload?.error) return `${name}อัปโหลดไม่สำเร็จ — ${payload.error}${s3 ? ` (${s3})` : ''}`
  return `${name}อัปโหลดไม่สำเร็จ (${status})`
}

/* Images preview inline — a quotation is far easier to recognise by sight than
 * by "SCR-20260806-mlaj.png". Everything else keeps a document glyph. The src
 * goes through the auth-gated proxy (same-origin, so the session cookie rides
 * along); a broken/expired object falls back to an icon rather than a torn
 * image. */
const isImage = (f) =>
  String(f?.type || '').startsWith('image/') ||
  /\.(png|jpe?g|gif|webp|bmp|avif|heic)$/i.test(String(f?.name || f?.location || ''))

function FileThumb({ file }) {
  const [broken, setBroken] = useState(false)
  if (isImage(file) && !broken) {
    return (
      <a href={fileUrl(file)} target="_blank" rel="noreferrer" className="shrink-0" title="เปิดดูรูป">
        <img src={fileUrl(file)} alt="" loading="lazy" onError={() => setBroken(true)}
          className="w-9 h-9 rounded-lg object-cover border border-slate-200 bg-slate-50" />
      </a>
    )
  }
  const Icon = isImage(file) ? ImageOff : (String(file?.type || '').includes('pdf') ? FileText : Paperclip)
  return (
    <span className="shrink-0 w-9 h-9 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center">
      <Icon size={15} className="text-slate-400" />
    </span>
  )
}

export default function PurchaseFiles({ po, files, onChange, disabled = false }) {
  const [busy, setBusy] = useState(null)         // 'upload' | uuid
  const [error, setError] = useState(null)
  const [renaming, setRenaming] = useState(null) // uuid
  const [draftName, setDraftName] = useState('')
  const [confirmFile, setConfirmFile] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [progress, setProgress] = useState(null)  // { done, total } while uploading
  const inputRef = useRef(null)
  // dragenter/dragleave fire for every child element the pointer crosses, so a
  // plain boolean flickers. Count enters minus leaves instead.
  const dragDepth = useRef(0)

  const list = Array.isArray(files) ? files : []

  async function save(next) {
    const r = await postJson('/api/procurement', { PO_number: po, file: next, file_set: true })
    onChange?.(Array.isArray(r?.file) ? r.file : next)
  }

  async function upload(fileList) {
    const picked = [...(fileList || [])]
    if (!picked.length) return
    setBusy('upload'); setError(null); setProgress({ done: 0, total: picked.length })
    try {
      let next = list
      for (const f of picked) {
        try {
          // Direct to Wasabi where CORS allows, else the old proxy — uploadDirect.js.
          next = [...next, toProcurementFile(await uploadFile('/api/purchase-upload', {}, f))]
        } catch (e) {
          // uploadError needs the FILE to name it, so the translation happens here
          // rather than in the outer catch where only the error survives.
          throw new Error(uploadError(e, f))
        }
        setProgress((p) => ({ done: (p?.done || 0) + 1, total: picked.length }))
      }
      await save(next)
    } catch (e) {
      setError(e.message || 'อัปโหลดไม่สำเร็จ')
    } finally {
      setBusy(null)
      setProgress(null)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  /* ── drag & drop ──────────────────────────────────────────────────────────
   * Accepts a whole selection at once — the same path as the file picker, which
   * already loops over the list. Only reacts when the drag actually carries
   * files: dragging selected text or a link over the drawer must not arm the
   * zone. preventDefault on dragover is what makes the drop legal at all;
   * without it the browser just navigates to the dropped file. */
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files')
  const canDrop = !disabled && busy !== 'upload'

  function onDragEnter(e) {
    if (!canDrop || !hasFiles(e)) return
    e.preventDefault()
    dragDepth.current += 1
    setDragging(true)
  }
  function onDragOver(e) {
    if (!canDrop || !hasFiles(e)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }
  function onDragLeave(e) {
    if (!canDrop) return
    e.preventDefault()
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDragging(false)
  }
  function onDrop(e) {
    if (!canDrop || !hasFiles(e)) return
    e.preventDefault()
    dragDepth.current = 0
    setDragging(false)
    upload(e.dataTransfer.files)
  }

  async function remove(f) {
    setConfirmFile(null)
    setBusy(f.uuid); setError(null)
    try {
      await save(list.filter((x) => x.uuid !== f.uuid))
      // Only drop the object once the record no longer points at it, so a failed
      // save can never leave a row referencing a file that is already gone.
      await apiDelete(fileUrl(f)).catch(() => {})
    } catch (e) {
      setError(e.message || 'ลบไม่สำเร็จ')
    } finally { setBusy(null) }
  }

  async function rename(f) {
    const name = draftName.trim()
    setRenaming(null)
    if (!name || name === f.name) return
    setBusy(f.uuid); setError(null)
    try {
      await save(list.map((x) => (x.uuid === f.uuid ? { ...x, name } : x)))
    } catch (e) {
      setError(e.message || 'เปลี่ยนชื่อไม่สำเร็จ')
    } finally { setBusy(null) }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[12.5px] font-medium text-slate-500">เอกสารแนบ ({list.length})</span>
        <button onClick={() => inputRef.current?.click()} disabled={disabled || busy === 'upload'}
          className="text-[12.5px] text-brand-600 hover:text-brand-700 disabled:opacity-40 flex items-center gap-1">
          {busy === 'upload' ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />}แนบไฟล์
        </button>
        <input ref={inputRef} type="file" multiple className="hidden"
          onChange={(e) => upload(e.target.files)} />
      </div>

      <div
        onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
        className={`relative rounded-xl transition ${dragging ? 'ring-2 ring-brand-400 ring-offset-2' : ''}`}
      >
        {dragging && (
          <div className="absolute inset-0 z-10 rounded-xl bg-brand-50/95 border-2 border-dashed border-brand-400 flex flex-col items-center justify-center pointer-events-none">
            <Upload size={20} className="text-brand-600" />
            <span className="text-[12.5px] font-medium text-brand-700 mt-1">วางไฟล์เพื่อแนบ</span>
          </div>
        )}

      {list.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 mb-2">
          {list.map((f) => (
            <div key={f.uuid || f.location} className="flex items-center gap-2.5 px-3 py-2">
              <FileThumb file={f} />
              {renaming === f.uuid ? (
                <>
                  <input value={draftName} autoFocus onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') rename(f); if (e.key === 'Escape') setRenaming(null) }}
                    className="flex-1 min-w-0 rounded-lg border border-brand-300 px-2 py-1 text-[12.5px] text-slate-800" />
                  <button onClick={() => rename(f)} className="p-1 text-emerald-600 hover:text-emerald-700"><Check size={14} /></button>
                  <button onClick={() => setRenaming(null)} className="p-1 text-slate-400 hover:text-slate-600"><X size={14} /></button>
                </>
              ) : (
                <>
                  <a href={fileUrl(f)} target="_blank" rel="noreferrer"
                    className="flex-1 min-w-0 text-[12.5px] text-slate-700 truncate hover:text-brand-700 hover:underline">
                    {f.name || f.location}
                  </a>
                  {busy === f.uuid ? <Loader2 size={13} className="animate-spin text-slate-400" /> : (
                    <>
                      <a href={fileUrl(f)} download className="p-1 text-slate-300 hover:text-brand-600" title="ดาวน์โหลด"><Download size={14} /></a>
                      {!disabled && (
                        <button onClick={() => { setRenaming(f.uuid); setDraftName(f.name || '') }}
                          className="p-1 text-slate-300 hover:text-brand-600" title="เปลี่ยนชื่อ"><Pencil size={13} /></button>
                      )}
                      {!disabled && (
                        <button onClick={() => setConfirmFile(f)} className="p-1 text-slate-300 hover:text-rose-600" title="ลบไฟล์"><Trash2 size={13} /></button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* The drop zone is always here, at full size. Hanging it off the empty
          state meant the target shrank to a couple of list rows exactly when a
          PO had files worth adding to. */}
      <button
        type="button" onClick={() => inputRef.current?.click()} disabled={disabled || busy === 'upload'}
        className="w-full rounded-xl border-2 border-dashed border-slate-200 bg-white/50 px-4 py-6 text-center text-[12.5px] text-slate-400 hover:border-brand-300 hover:text-brand-600 hover:bg-brand-50/40 transition disabled:pointer-events-none"
      >
        <Upload size={18} className="mx-auto mb-1 text-slate-300" />
        {list.length === 0 && <>ใบเสนอราคา · ใบแจ้งหนี้ · ใบเสร็จ<br /></>}
        <span className="text-[11.5px]">ลากไฟล์มาวางที่นี่ หรือคลิกเพื่อเลือก — แนบได้หลายไฟล์</span>
      </button>
      </div>

      {progress && progress.total > 1 && (
        <p className="mt-1.5 text-[11.5px] text-brand-600 flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> กำลังอัปโหลด {progress.done}/{progress.total} ไฟล์…
        </p>
      )}
      {error && <p className="mt-1.5 text-[12px] text-rose-600">{error}</p>}
      <p className="mt-1.5 text-[11.5px] text-slate-400">ลากไฟล์มาวางได้ · ไฟล์บันทึกทันทีที่แนบ (ไม่ต้องกดบันทึก)</p>

      {confirmFile && (
        <ConfirmDialog
          title="ลบไฟล์นี้ถาวร?"
          message={`“${confirmFile.name}” จะถูกลบออกจากใบนี้และจากที่เก็บไฟล์`}
          confirmLabel="ลบไฟล์" tone="danger"
          onConfirm={() => remove(confirmFile)}
          onCancel={() => setConfirmFile(null)}
        />
      )}
    </div>
  )
}
